/**
 * What strategy one item supports — for "Strategy it supports" on the item page.
 *
 * Owner model (2026-10-15): an item supports a strategic objective, and that
 * objective belongs to a strategy. The item can also be linked to a goal, but
 * only a goal under that objective. The strategy is shown, never linked.
 *
 * The link is the one the New item form and the Align app already write: an
 * objective_priority_mappings row (objective ↔ item, how strongly, and since
 * migration 924 optionally one goal under the objective). Here the rows are
 * grouped strategy → objective → goal, so the page reads from the top down.
 *
 * Reads only, through src/lib/strategyAlignment.ts. Nothing is fetched when
 * the workspace has Strategy off.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  listGoals,
  listMappingsByPriority,
  listObjectives,
  listStrategies,
  type Goal,
  type ObjectivePriorityMapping,
  type StrategicObjective,
  type Strategy,
} from '@/lib/strategyAlignment';

export type AlignmentStrength = ObjectivePriorityMapping['alignmentStrength'];

/** One objective the item supports, with every link row that names it. */
export interface ItemObjectiveLink {
  objective: StrategicObjective;
  /** The objectives above this one, the highest first. */
  above: StrategicObjective[];
  /** The strongest strength among its rows. */
  strength: AlignmentStrength;
  /** Every link row for this objective (removing the objective removes them all). */
  mappingIds: string[];
  /** Goals of this objective the item is linked to, each with its row. */
  linkedGoals: { goal: Goal; mappingId: string }[];
  /** The objective's other goals, shown for context. */
  otherGoals: Goal[];
  rationale: string | null;
}

/** A strategy and the objectives under it that the item supports. `strategy` null = objectives under no strategy. */
export interface ItemStrategyGroup {
  strategy: Strategy | null;
  objectives: ItemObjectiveLink[];
}

export interface ItemStrategy {
  groups: ItemStrategyGroup[];
  /** Everything that can be picked from: the workspace's objectives, goals and strategies. */
  objectives: StrategicObjective[];
  goals: Goal[];
  strategies: Strategy[];
  reload: () => void;
}

export const STRENGTH_ORDER: Record<AlignmentStrength, number> = { primary: 0, secondary: 1, indirect: 2 };

/** The objectives above one objective, highest first (a loop in the data stops the walk). */
export function objectivesAbove(o: StrategicObjective, byId: Map<string, StrategicObjective>): StrategicObjective[] {
  const chain: StrategicObjective[] = [];
  const seen = new Set<string>([o.id]);
  let parent = o.parentObjectiveId ? byId.get(o.parentObjectiveId) : undefined;
  while (parent && !seen.has(parent.id)) {
    chain.unshift(parent);
    seen.add(parent.id);
    parent = parent.parentObjectiveId ? byId.get(parent.parentObjectiveId) : undefined;
  }
  return chain;
}

/** The strategy an objective belongs to: its own, or the nearest objective above it that names one. */
export function strategyIdOf(o: StrategicObjective, byId: Map<string, StrategicObjective>): string | null {
  if (o.strategyId) return o.strategyId;
  return objectivesAbove(o, byId)
    .reverse()
    .find((a) => a.strategyId)?.strategyId ?? null;
}

/** Pure: shape the rows the page reads into strategy → objective → goal. Exported for tests. */
export function assembleItemStrategy(
  mappings: ObjectivePriorityMapping[],
  objectives: StrategicObjective[],
  goals: Goal[],
  strategies: Strategy[],
): ItemStrategyGroup[] {
  const byId = new Map(objectives.map((o) => [o.id, o]));
  const strategyById = new Map(strategies.map((s) => [s.id, s]));
  const goalById = new Map(goals.map((g) => [g.id, g]));

  const links = new Map<string, ItemObjectiveLink>();
  for (const m of mappings) {
    const objective = byId.get(m.objectiveId);
    // An objective this person cannot read, or one deleted since, is left out.
    if (!objective) continue;
    let link = links.get(objective.id);
    if (!link) {
      link = {
        objective,
        above: objectivesAbove(objective, byId),
        strength: m.alignmentStrength,
        mappingIds: [],
        linkedGoals: [],
        otherGoals: [],
        rationale: null,
      };
      links.set(objective.id, link);
    }
    link.mappingIds.push(m.id);
    if (STRENGTH_ORDER[m.alignmentStrength] < STRENGTH_ORDER[link.strength]) link.strength = m.alignmentStrength;
    if (!link.rationale && m.rationale?.trim()) link.rationale = m.rationale.trim();
    const goal = m.goalId ? goalById.get(m.goalId) : undefined;
    // Only a goal still under this objective counts as linked to it.
    if (goal && goal.objectiveId === objective.id && !link.linkedGoals.some((l) => l.goal.id === goal.id)) {
      link.linkedGoals.push({ goal, mappingId: m.id });
    }
  }
  for (const link of links.values()) {
    const linked = new Set(link.linkedGoals.map((l) => l.goal.id));
    link.otherGoals = goals.filter((g) => g.objectiveId === link.objective.id && !linked.has(g.id));
  }

  const byStrategy = new Map<string, ItemStrategyGroup>();
  for (const link of links.values()) {
    const sid = strategyIdOf(link.objective, byId);
    const strategy = sid ? strategyById.get(sid) ?? null : null;
    const key = strategy?.id ?? '';
    const group = byStrategy.get(key) ?? { strategy, objectives: [] };
    group.objectives.push(link);
    byStrategy.set(key, group);
  }
  const strongest = (g: ItemStrategyGroup) => Math.min(...g.objectives.map((o) => STRENGTH_ORDER[o.strength]));
  const groups = [...byStrategy.values()];
  for (const g of groups) {
    g.objectives.sort((a, b) => STRENGTH_ORDER[a.strength] - STRENGTH_ORDER[b.strength] || a.objective.name.localeCompare(b.objective.name));
  }
  // Strongest support first; objectives under no strategy last.
  return groups.sort(
    (a, b) => Number(!a.strategy) - Number(!b.strategy) || strongest(a) - strongest(b) || (a.strategy?.name ?? '').localeCompare(b.strategy?.name ?? ''),
  );
}

export function useItemStrategy(itemId: string, workspaceId: string | null, enabled: boolean): ItemStrategy | null {
  const [state, setState] = useState<Omit<ItemStrategy, 'reload'> | null>(null);
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!enabled || !workspaceId) {
      setState(null);
      return;
    }
    let live = true;
    Promise.all([listMappingsByPriority(itemId), listObjectives(workspaceId), listGoals(workspaceId), listStrategies(workspaceId)])
      .then(([mappings, objectives, goals, strategies]) => {
        if (!live) return;
        setState({ groups: assembleItemStrategy(mappings, objectives, goals, strategies), objectives, goals, strategies });
      })
      .catch(() => live && setState({ groups: [], objectives: [], goals: [], strategies: [] }));
    return () => {
      live = false;
    };
  }, [itemId, workspaceId, enabled, version]);

  return state ? { ...state, reload } : null;
}
